/**
 * Performance Optimization Module
 * - Lazy loads non-critical CSS
 * - Monitors Core Web Vitals
 * - Optimizes image loading
 */

(function() {
  'use strict';

  // Defer non-critical CSS loading
  function deferNonCriticalCSS() {
    const noncriticalCSS = document.querySelectorAll('link[data-defer-css]');
    noncriticalCSS.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      
      link.rel = 'preload';
      link.as = 'style';
      link.onload = function() {
        this.rel = 'stylesheet';
      };
      
      // Fallback for non-supporting browsers
      const script = document.createElement('script');
      script.textContent = `
        (function(){
          const link = document.querySelector('link[href="${href}"]');
          if (link) link.rel = 'stylesheet';
        }());
      `;
      document.body.appendChild(script);
    });
  }

  // Optimize image loading for lazy images
  function optimizeImageLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            
            // Load image
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            
            // Load background image
            if (img.dataset.background) {
              img.style.backgroundImage = `url(${img.dataset.background})`;
              img.removeAttribute('data-background');
            }
            
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });

      document.querySelectorAll('img[data-src], div[data-background]').forEach((img) => {
        imageObserver.observe(img);
      });
    }
  }

  // Monitor Core Web Vitals
  function monitorWebVitals() {
    if ('web-vital' in window) return;
    
    // Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          console.log('LCP:', lastEntry.renderTime || lastEntry.loadTime);
        });
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.error('LCP observer error:', e);
      }

      // First Input Delay (FID) / Interaction to Next Paint (INP)
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            console.log('FID/INP:', entry.processingDuration);
          });
        });
        observer.observe({ entryTypes: ['first-input', 'event'] });
      } catch (e) {
        console.error('FID observer error:', e);
      }

      // Cumulative Layout Shift (CLS)
      try {
        let cls = 0;
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (!entry.hadRecentInput) {
              cls += entry.value;
              console.log('CLS:', cls);
            }
          });
        });
        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.error('CLS observer error:', e);
      }
    }
  }

  // Reduce motion preferences
  function respectMotionPreferences() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    function handleMotionPreference(e) {
      if (e.matches) {
        document.documentElement.style.setProperty('--animation-duration', '0.01ms');
        document.documentElement.classList.add('reduce-motion');
      } else {
        document.documentElement.style.removeProperty('--animation-duration');
        document.documentElement.classList.remove('reduce-motion');
      }
    }
    
    handleMotionPreference(mediaQuery);
    mediaQuery.addListener(handleMotionPreference);
  }

  // Initialize all optimizations when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      deferNonCriticalCSS();
      optimizeImageLoading();
      respectMotionPreferences();
      monitorWebVitals();
    });
  } else {
    deferNonCriticalCSS();
    optimizeImageLoading();
    respectMotionPreferences();
    monitorWebVitals();
  }

  // Prefetch pages on link hover
  document.addEventListener('mouseover', (e) => {
    const link = e.target.closest('a[href]');
    if (link && link.hostname === window.location.hostname) {
      const href = link.getAttribute('href');
      const existingPrefetch = document.querySelector(`link[rel="prefetch"][href="${href}"]`);
      if (!existingPrefetch && !href.includes('#')) {
        const prefetchLink = document.createElement('link');
        prefetchLink.rel = 'prefetch';
        prefetchLink.href = href;
        document.head.appendChild(prefetchLink);
      }
    }
  }, { passive: true });
})();
